import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { RegisterData } from "@/types/auth";
import { navigate } from "wouter/use-browser-location";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { registrationTypes } from "@/constants/registrationTypes";
import TermsAndConditionsContent from "./TermsAndConditionsContent";

type ValidationErrors = {
  [key: string]: string;
};

const RegisterPage = () => {
  const { register, isRegistering } = useAuth();
  const [step, setStep] = useState(1);
  // const [, setAlertMessage] = useState<string | null>(null);
  // const [, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [hasViewedTerms, setHasViewedTerms] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    businessName: "",
    tin: "",
    registrationType: "BRN",
    registrationNumber: "",
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

  const validationPatterns = {
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    password:
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/,
    tin: /^[A-Za-z0-9]{9,14}$/,
  };

  const errorMessages = {
    required: "This field is required",
    email: "Please enter a valid email address",
    password:
      "Password must be at least 6 characters and include uppercase, lowercase, number and special character",
    confirmPassword: "Passwords do not match",
    tin: "TIN must be 9-14 alphanumeric characters",
    minLength: (length: number) => `Must be at least ${length} characters`,
    maxLength: (length: number) => `Must not exceed ${length} characters`,
    terms: "You must agree to the Terms & Conditions before creating an account.",
  };

  const validateField = (name: string, value: string): string => {
    if (!value && name !== "addressLine1" && name !== "addressLine2") {
      return errorMessages.required;
    }

    switch (name) {
      case "email":
      case "businessEmail":
        if (!validationPatterns.email.test(value)) {
          return errorMessages.email;
        }
        break;
      case "password":
        if (!validationPatterns.password.test(value)) {
          return errorMessages.password;
        }
        break;
      case "confirmPassword":
        if (value !== formData.password) {
          return errorMessages.confirmPassword;
        }
        break;
      case "businessName":
        if (value.length < 2) {
          return errorMessages.minLength(2);
        }
        if (value.length > 100) {
          return errorMessages.maxLength(100);
        }
        break;
      case "tin":
        if (!validationPatterns.tin.test(value)) {
          return errorMessages.tin;
        }
        break;
      case "registrationNumber":
        if (value.length > 20) {
          return "Registration Number must be not more than 20 characters.";
        }
        break;
    }

    return "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateAndSetError(name, value);
  };

  const validateAndSetError = (name: string, value: string) => {
    const errorMessage = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: errorMessage,
    }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
    validateAndSetError(
      name,
      formData[name as keyof typeof formData] as string
    );
  };

  const validateStep = () => {
    let isValid = true;
    const currentFields = getFieldsForCurrentStep();
    const newErrors: ValidationErrors = {};

    currentFields.forEach((field) => {
      const error = validateField(
        field,
        formData[field as keyof typeof formData] as string
      );
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    if (step == 2) {
      if (!hasViewedTerms) {
        newErrors["terms"] = "You must first view the Terms and Conditions";
        isValid = false;
      }
      if (!termsAccepted) {
        newErrors["terms"] = errorMessages.terms;
        isValid = false;
      }
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    const newTouched: { [key: string]: boolean } = {};
    currentFields.forEach((field) => {
      newTouched[field] = true;
    });
    setTouched((prev) => ({ ...prev, ...newTouched }));

    return isValid;
  };

  const getFieldsForCurrentStep = (): string[] => {
    if (step == 1) {
      return ["businessName", "tin", "registrationNumber"];
    } else {
      return ["email", "password", "confirmPassword"];
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep((prev) => prev + 1);
    }
  };

  const prevStep = () => setStep((prev) => prev - 1);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep()) {
      return;
    }

    if (!termsAccepted) {
      setErrors((prev) => ({
        ...prev,
        terms: errorMessages.terms,
      }));
      setTermsModalOpen(true);
      return;
    }

    const registerData: RegisterData = {
      businessProfile: {
        name: formData.businessName,
        tin: formData.tin,
        registrationType: formData.registrationType as "BRN",
        registrationNumber: formData.registrationNumber,
        usesBranches: true,
      },
      user: {
        email: formData.email,
        password: formData.password,
      },
    };
    register(registerData, {
      onSuccess: () => {
        setDialogMessage("Registration successful! Redirecting to login...");
        setOpenDialog(true);

        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 3000);
      },
      onError: (error: any) => {
        setDialogMessage(
          error?.response?.data?.message ||
            "Registration failed. Please try again."
        );
        setOpenDialog(true);
      },
    });
  };

  const renderTermsSection = () => (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <Button
          type="button"
          variant="link"
          className="text-blue-600 p-0"
          onClick={() => {
            setTermsModalOpen(true);
            setHasViewedTerms(true);
          }}
        >
          View Terms and Conditions
        </Button>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="terms"
          checked={termsAccepted}
          onCheckedChange={(checked) => {
            setTermsAccepted(!!checked);
            if (checked) {
              // Remove terms error when checkbox is checked
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.terms;
                return newErrors;
              });
            }
          }}
        />
        <Label
          htmlFor="terms"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          I accept the Terms and Conditions
        </Label>
      </div>

      {errors.terms && (
        <p className="text-red-500 text-sm mt-1">{errors.terms}</p>
      )}
    </div>
  );

  const showError = (fieldName: string) => {
    return touched[fieldName] && errors[fieldName] ? (
      <p className="text-red-500 text-sm mt-1">{errors[fieldName]}</p>
    ) : null;
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-blue-600 to-purple-700">
      <Card className="w-full max-w-md mx-4 bg-white/90 backdrop-blur-sm shadow-xl relative z-10">
        <CardHeader className="space-y-2">
          <CardTitle className="text-2xl text-center font-bold">
            Create Account
          </CardTitle>
          <p className="text-center text-gray-600">Step {step} of 2</p>
          <div className="flex justify-center items-center space-x-2 my-2">
            {[1, 2].map((pageStep) => (
              <div
                key={pageStep}
                className={`h-2 w-8 rounded-full transition-all duration-300 ${
                  step === pageStep ? "bg-blue-600 w-12" : "bg-gray-300"
                }`}
              ></div>
            ))}
          </div>
          <h2 className="text-lg font-semibold text-center">
            {step == 1 && "Business Profile"}
            {step == 2 && "User Information"}
          </h2>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            {step == 1 && (
              <div className="space-y-2">
                <div>
                  <Label htmlFor="businessName" required>
                    Business Name
                  </Label>
                  <Input
                    name="businessName"
                    type="text"
                    placeholder="Business Name"
                    value={formData.businessName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full ${
                      errors.businessName && touched.businessName
                        ? "border-red-500"
                        : ""
                    }`}
                    required
                  />
                  {showError("businessName")}
                </div>

                <div>
                  <Label htmlFor="tin" required>
                    TIN
                  </Label>
                  <Input
                    name="tin"
                    type="text"
                    placeholder="TIN"
                    value={formData.tin}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full ${
                      errors.tin && touched.tin ? "border-red-500" : ""
                    }`}
                    required
                  />
                  {showError("tin")}
                </div>
                <div>
                  <Label htmlFor="registrationType" required>
                    Registration Type
                  </Label>
                  <Select 
                    name="registrationType"
                    value="BRN"
                    onValueChange={(e) => setFormData((prev) => ({...prev, registrationType: e}))}
                    disabled
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="BRN" />
                    </SelectTrigger>
                    <SelectContent>
                      {registrationTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="registrationNumber" required>
                    Registration Number
                  </Label>
                  <Input
                    name="registrationNumber"
                    type="text"
                    placeholder="Registration Number"
                    value={formData.registrationNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full ${
                      errors.registrationNumber && touched.registrationNumber
                        ? "border-red-500"
                        : ""
                    }`}
                    required
                  />
                  {showError("registrationNumber")}
                </div>
              </div>
            )}

            {step == 2 && (
              <div className="space-y-2">
                <div>
                  <Label htmlFor="email" required>
                    Email
                  </Label>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.email && touched.email ? "border-red-500" : ""
                    }
                    required
                  />
                  {showError("email")}
                </div>

                <div>
                  <Label htmlFor="password" required>
                    Password
                  </Label>
                  <Input
                    name="password"
                    type="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.password && touched.password
                        ? "border-red-500"
                        : ""
                    }
                    required
                  />
                  {showError("password")}
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.confirmPassword && touched.confirmPassword
                        ? "border-red-500"
                        : ""
                    }
                    required
                  />
                  {showError("confirmPassword")}
                </div>
                {renderTermsSection()}
              </div>
            )}

            <div className="flex justify-between">
              {step > 1 && (
                <Button type="button" onClick={prevStep}>
                  Back
                </Button>
              )}
              {step < 2 ? (
                <Button type="button" onClick={nextStep}>
                  Next
                </Button>
              ) : (
                <Button type="submit" disabled={isRegistering || !termsAccepted}>{isRegistering ? "Registering..." : "Register"}</Button>
              )}
            </div>
          </form>
          <div className="mt-4 text-center text-sm">
            <a href="/login" className="text-blue-600 hover:underline">
              Already have an account? Log in
            </a>
          </div>

          <AlertDialog open={openDialog} onOpenChange={setOpenDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Registration</AlertDialogTitle>
                <AlertDialogDescription>{dialogMessage}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <Button onClick={() => setOpenDialog(false)}>OK</Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>

        <Dialog open={termsModalOpen} onOpenChange={setTermsModalOpen}>
          <DialogContent className="max-h-[85vh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Terms and Conditions</DialogTitle>
              <DialogDescription className="sr-only">
                The terms governing your use of the QuickBill platform.
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto pr-1">
              <TermsAndConditionsContent />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setTermsModalOpen(false)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setTermsAccepted(true);
                  setTermsModalOpen(false);
                }}
              >
                Accept Terms
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
};

export default RegisterPage;
